import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useABTest } from '../../../hooks/useABTest';

export default function Pricing() {
  const pricingTest = useABTest('pricing-display-test');

  const plans = [
    {
      name: 'Basic',
      price: 99,
      description: 'Parfait pour démarrer',
      features: [
        '1000 conversations/mois',
        'Support email',
        'Intégrations basiques',
        'Rapports mensuels',
        'API accès',
        '1 utilisateur',
        'Formation en ligne',
        'Mises à jour incluses'
      ],
      popular: false
    },
    {
      name: 'Pro',
      price: 199,
      oldPrice: 249,
      description: 'Le plus populaire',
      features: [
        '10 000 conversations/mois',
        'Support prioritaire 24/7',
        'Toutes les intégrations',
        'Rapports en temps réel',
        'API illimitée',
        '5 utilisateurs',
        'Formation personnalisée',
        'Mises à jour prioritaires',
        'Agent dédié',
        'Personnalisation avancée'
      ],
      popular: true
    },
    {
      name: 'Elite',
      price: 399,
      description: 'Pour les grandes entreprises',
      features: [
        'Conversations illimitées',
        'Support VIP dédié',
        'Intégrations sur mesure',
        'Analytics avancés',
        'API prioritaire',
        'Utilisateurs illimités',
        'Onboarding premium',
        'Développement custom',
        'SLA garanti 99.9%',
        'Account manager dédié',
        'Consulting stratégique',
        'White label disponible'
      ],
      popular: false
    }
  ];

  const showAnnualPricing = pricingTest.config?.showAnnual || false;
  const ctaText = pricingTest.config?.ctaText || 'Commencer Maintenant';

  const handleCTAClick = (planName: string) => {
    pricingTest.trackConversion(true, { plan: planName, variant: pricingTest.variantId });
  };

  return (
    <section id="pricing" className="py-32 bg-gradient-to-b from-cyber-dark to-black relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyber-purple/10 via-transparent to-transparent"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl lg:text-6xl font-display font-bold text-white mb-4">
            Choisissez Votre Formule
          </h2>
          <p className="text-2xl text-cyber-blue font-display">
            {showAnnualPricing ? 'Tarif Annuel (2 mois offerts)' : 'Par Agent'}
          </p>
          <p className="text-gray-400 font-body mt-4">
            Activez autant d'agents que nécessaire • Réduction multi-agents disponible
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => {
            const displayPrice = showAnnualPricing ? Math.round(plan.price * 10) : plan.price;
            const displayOldPrice = showAnnualPricing && plan.oldPrice ? Math.round(plan.oldPrice * 10) : plan.oldPrice;
            
            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-3xl p-8 ${
                  plan.popular
                    ? 'bg-gradient-to-br from-cyber-purple/15 to-cyber-blue/15 border-2 border-cyber-blue scale-105 shadow-2xl shadow-cyber-purple/40'
                    : 'bg-black/25 border border-white/10'
                } backdrop-blur-xl transition-all hover:scale-105`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-body font-bold text-sm rounded-full">
                      Populaire
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <div className={`inline-block px-4 py-1 rounded-full mb-4 ${
                    plan.popular ? 'bg-cyber-blue/20 text-cyber-blue' : 'bg-gray-700 text-gray-300'
                  } font-body text-sm font-semibold`}>
                    {plan.name}
                  </div>
                  
                  <div className="mb-2">
                    {displayOldPrice && (
                      <span className="text-2xl text-gray-500 line-through mr-2">
                        {displayOldPrice}€
                      </span>
                    )}
                    <span className="text-5xl font-display font-bold text-white">
                      {displayPrice}€
                    </span>
                    <span className="text-gray-400 font-body">
                      {showAnnualPricing ? '/an' : '/mois'}
                    </span>
                  </div>
                  
                  <p className="text-gray-400 font-body text-sm">{plan.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <i className={`ri-checkbox-circle-fill text-xl flex-shrink-0 ${
                        plan.popular ? 'text-cyber-blue' : 'text-gray-500'
                      }`}></i>
                      <span className="text-gray-300 font-body text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/signup"
                  onClick={() => handleCTAClick(plan.name)}
                  className={`block w-full py-4 text-center font-body font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer min-h-[48px] flex items-center justify-center ${
                    plan.popular
                      ? 'bg-gradient-to-r from-cyber-blue to-cyber-purple text-white hover:shadow-2xl hover:shadow-cyber-blue/50'
                      : plan.name === 'Elite'
                      ? 'border-2 border-yellow-600 text-yellow-600 hover:bg-yellow-600/20'
                      : 'border-2 border-white/40 text-white hover:bg-white/10'
                  }`}
                >
                  {plan.popular ? 'Commencer mon essai gratuit' : 'Choisir ' + plan.name}
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-gray-400 font-body mb-4">
            🎁 <strong className="text-white">Réduction de 20%</strong> si vous activez 3 agents ou plus
          </p>
          <p className="text-gray-400 font-body text-sm">
            Tous les plans incluent : Essai gratuit 14 jours • Sans engagement • Annulation à tout moment
          </p>
        </motion.div>
      </div>
    </section>
  );
}